/** @file Main file of the application. */
import * as detectBrowser from 'detect-browser';
import moment from 'moment';
import Vue from 'vue';
import { Maybe } from '@codegrade/maybe';
import { datetime } from '@codegrade/utils';
import './style/tailwind.css';
import './style/index.less';
import App from '@/App';
import { BREAKPOINTS } from '@/constants';
import * as utils from '@/utils';
import client, { unauthorizedClient } from './client';
import router from './router';
import * as store from './store';
import 'reflect-metadata';
import './register-components';
import * as directives from './directives';
window.cgLogging = { doEventLogging: false, doMetricLogging: false };
Vue.config.productionTip = false;
Vue.directive('focus', {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    inserted(el, other) {
        const value = other.value;
        if (value != null && typeof value !== 'boolean') {
            // eslint-disable-next-line no-console
            console.warn('Wrong type passed to focus directive', value);
        }
        if (value !== false) {
            el.focus();
        }
    },
});
Vue.directive('visible', directives.visible);
moment.relativeTimeThreshold('h', 48);
moment.defineLocale('en-original', {
    parentLocale: 'en',
});
moment.updateLocale('en', {
    relativeTime: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        past(input) {
            return input === 'just now' ? input : `${input} ago`;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        future(input) {
            return input === 'just now' ? input : `in ${input}`;
        },
        s: 'just now',
        ss: '%d seconds',
        m: 'a minute',
        mm: '%d minutes',
        h: 'an hour',
        hh: '%d hours',
        d: 'a day',
        dd: '%d days',
        M: 'a month',
        MM: '%d months',
        y: 'a year',
        yy: '%d years',
    },
    calendar: {
        lastDay: '[yesterday at] LT',
        sameDay: '[today at] LT',
        nextDay: '[tomorrow at] LT',
        lastWeek: '[last] dddd [at] LT',
        nextWeek: 'dddd [at] LT',
        sameElse: 'YYYY-MM-DD HH:mm',
    },
});
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$inLTI', false, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$ltiProvider', null, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$LTICourseId', null, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$LTIAssignmentId', null, null, true);
try {
    Vue.prototype.$devMode = process.env.NODE_ENV === 'development';
}
catch (e) {
    Vue.prototype.$devMode = false;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
Vue.prototype.$routeParamAsId = function $routeParamAsId(name) {
    const res = utils.parseOrKeepFloat(this.$route.params[name]);
    return Number.isNaN(res) ? undefined : res;
};
Object.defineProperty(Vue.prototype, '$propsTyped', {
    /** @returns See {@link Vue['$propsTyped']}. */
    get() {
        return this.$props;
    },
});
Vue.prototype.$afterRerender = utils.doubleRequestAnimationFrame;
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
Vue.prototype.$waitForRef = async function waitForRef(refName, retries = 5) {
    for (let i = 0; i < retries; i++) {
        if (this.$refs[refName] == null) {
            // eslint-disable-next-line no-await-in-loop
            await this.$afterRerender();
        }
        else {
            break;
        }
    }
    return this.$refs[refName];
};
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
Vue.prototype.$withRef = async function withRef(refName, f, retries = 5) {
    const ref = await this.$waitForRef(refName, retries);
    if (ref != null) {
        return Maybe.of(f(ref));
    }
    return Maybe.empty();
};
/**
 * Focus the component or element with the given reference.
 *
 * @param this Vue instance, used to call prototype methods on.
 * @param ref The reference name of the component or element to focus.
 * @param waitForNextTick Whether to wait for the next render tick to find the ref, default `true`.
 * @returns Whether we have succeeded in finding the reference and focussing it.
 */
Vue.prototype.$focusRef = async function focusRef(ref, waitForNextTick = true) {
    if (waitForNextTick) {
        await this.$nextTick();
    }
    const foundAndFocused = await this.$withRef(ref, component => {
        const elem = component instanceof HTMLElement ? component : component.$el;
        if (elem instanceof HTMLElement) {
            elem.focus();
            return true;
        }
        return false;
    });
    // If we did *not* find the element, withRef will return Nothing.
    // If we did find the element, but it was not a HTML element, we failed to focus it, Maybe<false>.
    // Only if we did find the element, and focussed it, Maybe<true>.
    return foundAndFocused.extract() === true;
};
/** @returns The Root Instance typed with root specific properties. */
Vue.prototype.$getRootInstance = function $getRootInstance() {
    return this.$root;
};
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
Vue.prototype.$addToast = function $addToast(toast) {
    this.$root.$emit('cg::app::toast', toast);
};
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
Vue.prototype.$deleteToast = function $addToast(toast) {
    this.$root.$emit('cg::app::delete-toast', toast);
};
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
Vue.prototype.$updateNow = function updateNow() {
    this.$root.$emit('cg::root::update-now');
};
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$windowWidth', window.innerWidth, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$windowHeight', window.innerHeight, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$isSmallWindow', false, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$isMediumWindow', false, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$isLargeWindow', false, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$isXLargeWindow', false, null, true);
/** Our customised Vue instance. */
export const MyVue = Vue.extend({
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        const now = datetime.momentUtc(Maybe.empty());
        return {
            // `Now` and `epoch` both contain the current time. They are
            // the same, except that `epoch` is recalculated every second
            // while `now` is set only every minute. We do not want `now`
            // to be updated as often because that would trigger a redraw
            // of the sidebar every second.
            now,
            epoch: now,
            caughtErrors: [],
            $loadFullNotifications: false,
        };
    },
    /**
     * Initialize the root Vue instance.
     *
     * Set up store/client callbacks.
     * Window resize listeners.
     * Time-update intervals.
     * Scheduled update checks.
     */
    created() {
        store.onVueCreated(this);
        client.onVueCreated(this);
        this.updateWindowSizes();
        window.addEventListener('resize', () => {
            this.updateWindowSizes();
        });
        this.$on('cg::root::update-now', () => this.updateTimes());
        // We use less than a minute for now and less than a second for epoch so
        // that they can be used to nicely count minutes and seconds elapsed
        // respectively. The exact amount less is arbitrary.
        setInterval(() => {
            const now = this.getCurrentTime();
            this.now = now;
            this.epoch = now.clone();
        }, 59000);
        setInterval(() => {
            const now = this.getCurrentTime();
            this.epoch = now;
            // This is a way to ensure that $now is never after $epoch. This also guards against any
            // accidental mutations of $now.
            if (this.now.valueOf() > now.valueOf()) {
                const msg = `$now was ${this.now.toISOString()} at ${now.toISOString()}, resetting it.`;
                if (IS_PRODUCTION) {
                    // eslint-disable-next-line no-console
                    console.warn(msg);
                }
                else {
                    throw new Error(msg);
                }
                this.now = now.clone();
            }
        }, 750);
        this._scheduleCheckForUpdates();
    },
    computed: {
        /** @returns A dict with information about the browser. */
        _browser() {
            return detectBrowser.detect();
        },
        /** @returns Whether the app is running in a Safari browser. */
        isSafari() {
            return this._browser?.name === 'safari';
        },
        /** @returns Whether the app is running in a Chrome, Chromium, or Edge browser. */
        isChrome() {
            const chromiums = ['chrome', 'edge-chromium'];
            return chromiums.includes(this._browser?.name);
        },
        /** @returns Whether the browser is running on Linux. */
        isLinux() {
            return this._browser?.os?.toLowerCase() === 'linux';
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        $now() {
            return this.now;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        $epoch() {
            return this.epoch;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        serverTimeOffset() {
            return store.SiteSettingsStore.getServerTimeOffset();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getCurrentTime() {
            return store.SiteSettingsStore.currentTimeGetter();
        },
    },
    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        updateWindowSizes() {
            const { innerWidth, innerHeight } = window;
            // Ignore the typescript errors below because the props are declared as readonly
            // to prevent them from being updated in other components.
            // @ts-expect-error: This is declared as readonly.
            this.$windowHeight = innerHeight;
            // @ts-expect-error: This is declared as readonly.
            this.$windowWidth = innerWidth;
            // @ts-expect-error: This is declared as readonly.
            this.$isSmallWindow = innerWidth <= BREAKPOINTS.smallWidth;
            // @ts-expect-error: This is declared as readonly.
            this.$isMediumWindow = innerWidth >= BREAKPOINTS.mediumWidth;
            // @ts-expect-error: This is declared as readonly.
            this.$isLargeWindow = innerWidth >= BREAKPOINTS.largeWidth;
            // @ts-expect-error: This is declared as readonly.
            this.$isXLargeWindow = innerWidth >= BREAKPOINTS.xlargeWidth;
            document.documentElement.style.setProperty('--vh', `${innerHeight / 100}px`);
        },
        /**
         * Get the current time with a high enough precision to do relative calculations compared to
         * a passed `base`.
         *
         * @param base The base to which you want to compare now, if this is close to `$now` we will
         * return `$epoch` for a higher precision.
         * @returns A moment that represents now with a good enough precision.
         */
        $getNow(base, opts) {
            // We need 60 seconds extra as `this.$now` might be 60 seconds off.
            const secondsDiff = (opts?.secondsPrecisionNeeded ?? 60) + 60;
            if (!base.isValid() ||
                datetime.diff(moment.utc(), base).abs().asSeconds() > secondsDiff) {
                return this.$now;
            }
            return this.$epoch;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        updateTimes() {
            const now = this.getCurrentTime();
            this.epoch = now;
            this.now = now;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        connectionError() {
            this.$addToast({
                tag: 'ConnectionError',
                title: 'Connection error',
                message: 'There was an error connecting to the server... Please try again later.',
                variant: 'danger',
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        _checkForUpdates() {
            if (!IS_PRODUCTION) {
                return;
            }
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            const buildUrl = (path) => utils.buildUrl(path, { query: { cacheBuster: Math.random() } });
            const responses = Object.freeze({
                200: (response) => response.text(),
            });
            unauthorizedClient
                .rawRequest({
                method: 'GET',
                path: buildUrl(['static', 'commitHash']),
                responses,
            })
                .then(remoteCommit => {
                if (COMMIT_HASH !== remoteCommit) {
                    this.$emit('cg::app::toast', {
                        tag: 'UpdateAvailable',
                        title: 'CodeGrade update available!',
                        message: 'An updated version of CodeGrade is available. Please click here to reload the page and start using the latest version!',
                        href: '#',
                        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
                        onClick() {
                            window.location.reload();
                        },
                    });
                }
                else {
                    this._scheduleCheckForUpdates();
                }
            }, () => this._scheduleCheckForUpdates());
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        _scheduleCheckForUpdates() {
            setTimeout(() => {
                this._checkForUpdates();
            }, 10 * 60 * 1000);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        handleHttpError(err, status, method, url, authHeader) {
            switch (status) {
                case 401: {
                    if (method == null ||
                        url == null ||
                        method.toLowerCase() !== 'get' ||
                        url.startsWith('/api/v1/login') ||
                        router.currentRoute?.meta?.loginRequired !== true) {
                        return;
                    }
                    const storeToken = store.UserStore.dangerousSession().extract()?.accessToken;
                    if (storeToken != null &&
                        storeToken !== authHeader?.replace(/^Bearer\s*/, '')) {
                        return;
                    }
                    if (router.currentRoute.name !== 'login') {
                        this.$router.logoutAndRedirectAfterLogin();
                    }
                    return;
                }
                case 504: {
                    const msg = err?.data?.message;
                    if (msg == null) {
                        throw new Error('The server is very busy right now. Please try again in a moment.');
                    }
                    else {
                        return;
                    }
                }
                case undefined:
                case null:
                    this.connectionError();
                    break;
                default:
                    break;
            }
        },
    },
    watch: {
        isSafari: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            handler() {
                if (this.isSafari) {
                    document.body.classList.add('is-safari');
                }
                else {
                    document.body.classList.remove('is-safari');
                }
            },
        },
        serverTimeOffset: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            handler() {
                this.updateTimes();
            },
        },
    },
});
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const makeApp = () => new MyVue({
    el: '#app',
    router,
    store: store.store,
    /** @returns The main element of CodeGrade. */
    render() {
        return this.$createElement(App);
    },
});
